<template>
    <div>
        <form class="planner-content" method="get" action="https://www.google.com/maps/" target="google-maps" @submit.prevent="submitItinerary">
            <div class="address-inputs-wrapper">
                <div class="address-inputs">
                    <div class="field-wrapper">
                        <label for="departure-address">{{ t('departure_address') }}</label>
                        <div class="location-input-group">
                            <AutocompleteInput
                                id="departure-address"
                                v-model="positionDep"
                                :options="options"
                                :placeholder="getPlaceHolder('dep')"
                                :addresses="places"
                                :loading="autocompleteLoading"
                                :disabled="coordinates.dep === defaultPosition && coordinates.dep != null"
                                type="text"
                                :relative="false"
                                :hint="t('no_results_hint')"
                            >
                                <template #address="{ result, close }">
                                    <button
                                        type="button"
                                        class="address-result-button"
                                        @click="() => setDepAddress(result, close)"
                                    >
                                        <p class="results-item-name">
                                            <template v-if="result.address.house_number !== 0">
                                                {{ result.address.house_number }}
                                            </template>
                                            {{ result.address.name }}
                                        </p>
                                        <small class="results-item-desc">
                                            {{ result.address.administrative_regions[0].name }}
                                        </small>
                                    </button>
                                </template>
                                <template #arret="{ result }">
                                    <RouterLink
                                        class="stop-result-link"
                                        :to="{
                                            name: 'stop-single',
                                            params: {
                                                stop: slugify(`${result.entity_id}-${result.entity_title}`),
                                                date,
                                            },
                                        }"
                                    >
                                        <div>
                                            <p class="results-item-name">
                                                {{ result.entity_id }} -
                                                <span v-html="result.entity_title"></span>
                                            </p>
                                            <small class="results-item-desc" v-html="result.description_stop"></small>
                                        </div>
                                        <div v-if="result.accesssibility === '1'">
                                            <span class="visuallyhidden">{{ $t('a11y.accessible.accessible_stop') }}</span>
                                            <img src="@@/img/accessible.svg" width="18" height="18" />
                                        </div>
                                        <span v-else class="visuallyhidden">
                                            {{ $t('a11y.accessible.not_accessible_stop') }}
                                        </span>
                                    </RouterLink>
                                </template>
                            </AutocompleteInput>
                            <input
                                :value="depAddress !== '' ? depAddress : coordinates.dep"
                                name="saddr"
                                type="hidden"
                            />
                            <button
                                v-if="(defaultAddressUse === 'arr' && defaultAddress) || !defaultAddress"
                                class="geolocation-button"
                                type="button"
                                @click="locateUser('dep')"
                            >
                                <IconGeolocation width="24" height="24" />
                                <span class="visuallyhidden">{{ t('user_position') }}</span>
                            </button>
                        </div>
                    </div>
                    <div class="field-wrapper">
                        <label for="arrival-address">{{ t('arrival_address') }}</label>
                        <div class="location-input-group">
                            <AutocompleteInput
                                id="arrival-address"
                                v-model="positionArr"
                                :options="options"
                                :placeholder="getPlaceHolder('arr')"
                                :addresses="places"
                                :loading="autocompleteLoading"
                                :disabled="coordinates.arr === defaultPosition && coordinates.arr != null"
                                type="text"
                                :relative="false"
                                :hint="t('no_results_hint')"
                            >
                                <template #address="{ result, close }">
                                    <button
                                        type="button"
                                        class="address-result-button"
                                        @click="() => setArrAddress(result, close)"
                                    >
                                        <p class="results-item-name">
                                            <template v-if="result.address.house_number !== 0">
                                                {{ result.address.house_number }}
                                            </template>
                                            {{ result.address.name }}
                                        </p>
                                        <small class="results-item-desc">
                                            {{ result.address.administrative_regions[0].name }}
                                        </small>
                                    </button>
                                </template>
                                <template #arret="{ result }">
                                    <RouterLink
                                        class="stop-result-link"
                                        :to="{
                                            name: 'stop-single',
                                            params: {
                                                stop: slugify(`${result.entity_id}-${result.entity_title}`),
                                                date,
                                            },
                                        }"
                                    >
                                        <div>
                                            <p class="results-item-name">
                                                {{ result.entity_id }} -
                                                <span v-html="result.entity_title"></span>
                                            </p>
                                            <small class="results-item-desc" v-html="result.description_stop"></small>
                                        </div>
                                        <div v-if="result.accesssibility === '1'">
                                            <span class="visuallyhidden">{{ $t('a11y.accessible.accessible_stop') }}</span>
                                            <img src="@@/img/accessible.svg" width="18" height="18" />
                                        </div>
                                        <span v-else class="visuallyhidden">
                                            {{ $t('a11y.accessible.not_accessible_stop') }}
                                        </span>
                                    </RouterLink>
                                </template>
                            </AutocompleteInput>
                            <input
                                :value="arrAddress !== '' ? arrAddress : coordinates.arr"
                                name="daddr"
                                type="hidden"
                            />
                            <button
                                v-if="(defaultAddressUse === 'dep' && defaultAddress) || !defaultAddress"
                                class="geolocation-button"
                                type="button"
                                @click="locateUser('arr')"
                            >
                                <IconGeolocation width="24" height="24" />
                                <span class="visuallyhidden">{{ t('user_position') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
                <button type="button" class="reverse-button" @click="reverseAddr">
                    <IconReverse width="30px" height="20px" />
                    <span class="visuallyhidden">{{ t('reverse_address') }}</span>
                </button>
            </div>
            <input type="hidden" name="dirflg" value="r" />
            <button type="submit" class="button submit-button">
                {{ t('get_directions') }}
                <span>
                    <IconGoogle width="16" height="16" />
                    <span class="visuallyhidden">Google Maps</span>
                </span>
            </button>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import { format } from '@@/js/utils/dateFns';
import navitia from '@@/js/api/navitia';
import slugify from '@@/js/utils/slugify';
import AutocompleteInput from '@/shared/AutocompleteInput';

import IconGeolocation from '@@/icons/geolocation.svg';
import IconGoogle from '@@/icons/google.svg';
import IconReverse from '@@/icons/reverse.svg';

const props = defineProps({
    defaultPosition: {
        type: String,
        default: null,
    },
    defaultAddress: {
        type: String,
        default: null,
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            departure_address: 'Adresse de départ',
            user_position: 'Utiliser ma position',
            arrival_address: "Adresse d'arrivée",
            reverse_address: 'Inverser les adresses',
            get_directions: "Calculer l'itinéraire",
            user_been_located:
                "Vous avez bien été localisé, votre position sera utilisée comme adresse de départ|Vous avez bien été localisé, votre position sera utilisée comme adresse d'arrivée",
            addresses_been_reversed: 'Les addresses ont été inversées',
            my_position: 'Ma position',
            no_results_hint: 'Inscrivez le numéro civique et le nom de la rue pour obtenir des résultats',
        },
        en: {
            departure_address: 'Departure address',
            user_position: 'Use my position',
            arrival_address: 'Arrival address',
            reverse_address: 'Reverse addresses',
            get_directions: 'Get directions',
            user_been_located:
                'You have been successfully located, your position will be used for departure address|You have been successfully located, your position will be used for arrival address',
            addresses_been_reversed: 'Addresses have been reversed',
            my_position: 'My position',
            no_results_hint: 'Enter the civic number and street name to get results',
        },
    },
});

const defaultAddressUse = ref(props.defaultAddress ? 'dep' : null);
const options = ref({ threshold: 0.1 });
const places = ref([]);
const coordinates = ref({ dep: props.defaultPosition || null, arr: null });
const depAddress = ref('');
const arrAddress = ref('');
const autocompleteLoading = ref(false);

const positionDep = computed({
    get() {
        return coordinates.value.dep ? '' : depAddress.value;
    },
    set(value) {
        coordinates.value.dep = null;
        depAddress.value = value;
    },
});

const positionArr = computed({
    get() {
        return coordinates.value.arr ? '' : arrAddress.value;
    },
    set(value) {
        coordinates.value.arr = null;
        arrAddress.value = value;
    },
});

watch(positionDep, async (newValue) => {
    if (newValue === '') {
        places.value = [];
        return;
    }
    try {
        places.value = await navitia.getPlaces(newValue);
    } catch (e) {}
});

watch(positionArr, async (newValue) => {
    if (newValue === '') {
        places.value = [];
        return;
    }
    try {
        places.value = await navitia.getPlaces(newValue);
    } catch (e) {}
});

function setDepAddress(result, close) {
    positionDep.value = `${result.address.label}`;
    nextTick(() => {
        close();
    });
}

function setArrAddress(result, close) {
    positionArr.value = `${result.address.label}`;
    nextTick(() => {
        close();
    });
}

function reverseAddr() {
    const coordDep = coordinates.value.dep;
    const coordArr = coordinates.value.arr;
    coordinates.value.dep = coordArr;
    coordinates.value.arr = coordDep;
    const depAddr = depAddress.value;
    const arrAddr = arrAddress.value;
    depAddress.value = arrAddr;
    arrAddress.value = depAddr;
    if (props.defaultAddress) {
        if (defaultAddressUse.value === 'dep') {
            defaultAddressUse.value = 'arr';
        } else {
            defaultAddressUse.value = 'dep';
        }
    }
}

function getPlaceHolder(direction) {
    if (direction === 'dep') {
        if (coordinates.value.dep === props.defaultPosition) {
            return props.defaultAddress;
        } else {
            return coordinates.value.dep ? t('my_position') : '';
        }
    } else if (coordinates.value.arr === props.defaultPosition) {
        return props.defaultAddress;
    } else {
        return coordinates.value.arr ? t('my_position') : '';
    }
}

function locateUser(direction) {
    navigator.geolocation.getCurrentPosition(
        (position) => {
            if (direction === 'dep') {
                coordinates.value.dep = `${position.coords.latitude}, ${position.coords.longitude}`;
                depAddress.value = '';
            } else {
                coordinates.value.arr = `${position.coords.latitude}, ${position.coords.longitude}`;
                arrAddress.value = '';
            }
        },
        (error) => {
            console.log(error);
        },
    );
}

function submitItinerary(event) {
    window.dataLayer?.push({
        event: 'route_search',
        today: format(new Date(), 'MM/dd/yyyy'),
    });
    event.target.submit();
}
</script>

<style lang="scss" scoped>
.planner-content {
    padding: 16px;
    @include breakpoint(medium) {
        padding: 24px;
    }
    :deep(.results-list) {
        z-index: 13;
    }
}
.address-inputs-wrapper {
    display: flex;
    align-items: center;
}
.address-inputs {
    flex-grow: 1;
}
.location-input-group {
    position: relative;
    :deep(.autocomplete-input) {
        padding-right: 40px;
        @include breakpoint(medium) {
            padding-right: 50px;
        }
    }
}
.geolocation-button,
.reverse-button {
    flex-shrink: 0;
    border: 0;
    background-color: transparent;
    width: 30px;
    svg {
        fill: $color-blue-60;
    }
    @include breakpoint(medium) {
        width: 40px;
    }
}
.geolocation-button {
    z-index: 12;
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
}
.reverse-button {
    transform: rotate(90deg);
}
.results-item-name {
    @include rem-typo(18px);
    margin-bottom: -8px;
}
.results-item-desc {
    @include rem-typo(12px);
    color: $color-black-50;
}
.address-result-button {
    @include accessible-focus;
    width: 100%;
    padding: 8px 16px;
    line-height: 1.5;
    text-align: left;
    background: transparent;
    border: 0;
}
.submit-button {
    width: calc(100% - 30px);
    @include breakpoint(medium) {
        width: calc(100% - 40px);
    }
}
</style>
